import { Box, Typography, Button, Avatar, Chip, Stack } from "@mui/material";
import EditIcon from "@/assets/svg/EditIcon";
import CalendarSvg from "@/assets/svg/CalendarSvg";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import { styles } from "./stylesProfileBox";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useFollowUserMutation,
  useLazyGetUniversalSearchQuery,
  useUnfollowUserMutation,
} from "@/store/api/userApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import FollowersModal from "./FollowersModal/FollowersModal";

import theme from "@/theme";
import EditProfileModal from "@/components/UI/modal/EditProfile/EditProfile";
import PremiumCheckmark from "@/assets/svg/PremiumCheckmark";
import TopEarnerBadge from "@/components/UI/TopEarnerBadge/TopEarnerBadge";
import { RequireAuth } from "@/components/RequireAuth";
import SendFundsSvg from "@/assets/svg/SendFundsSvg";
import SendFundsZap from "@/components/UI/modal/Wallet/LikeZap/SendFundsZapModal";
import LikesSvg from "@/assets/svg/LikesSvg";
import ZapsSvg from "@/assets/svg/ZapsSvg";
import TrandingSvg from "@/assets/svg/TrandingSvg";
import DollarCoinSvg from "@/assets/svg/DollarCoinSvg";
import TradingMentorSvg from "@/assets/svg/TradingMentorSvg";
import { formatMoney } from "@/helpers/formatMoney";

interface ProfileBoxProps {
  Edit?: boolean;
  avatarUrl: string;
  likesCount: number;
  zapCount: number;
  earnings: string;
  avgLikesPerPost: string;
  following: number;
  followers: number;
  badge?: string;
  username: string;
  bio: string;
  memberSince: string;
  tradingStyle: string;
  tradingMentor: string;
  isFollowing: boolean;
  targetUid: string;
  refetchUser?: () => void;
  isPremium: boolean;
  socialMedia: { url: string; title?: string }[];
  totalEarned?: number;
  topEarnerRank?: number;
}

export const ProfileBox: React.FC<ProfileBoxProps> = ({
  Edit,
  zapCount,
  avatarUrl,
  likesCount,
  // earnings,
  // avgLikesPerPost,
  following,
  followers,
  // badge,
  username,
  bio,
  memberSince,
  tradingStyle,
  tradingMentor,
  isFollowing,
  targetUid,
  refetchUser,
  isPremium,
  socialMedia,
  totalEarned,
  topEarnerRank,
}) => {
  const navigate = useNavigate();
  const formattedMemberSince = new Date(memberSince).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const [followUser] = useFollowUserMutation();
  const [unfollowUser] = useUnfollowUserMutation();
  const [isFollowingState, setIsFollowingState] = useState(isFollowing);
  const [followersCount, setFollowersCount] = useState(followers);
  const [searchUser] = useLazyGetUniversalSearchQuery();
  const [toast, setToast] = useState<{ message: string; success?: boolean } | null>(null);
  const [followersModalOpen, setFollowersModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<"followers" | "following">("followers");
  const openFollowersModal = (tab: "followers" | "following") => {
    setModalTab(tab);
    setFollowersModalOpen(true);
  };
  const [showSendModal, setShowSendModal] = useState(false);

  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);

  const handleEditProfileSave = async () => {
    try {
      setToast({ message: "Subscription successfully cancelled.", success: true });
    } catch (error: any) {
      setToast({
        message: error?.data?.message || "An error occurred while cancelling the subscription.",
        success: false,
      });
    }
  };

  const handleFollowClick = async () => {
    if (!targetUid) return;

    const previousState = isFollowingState;
    const previousFollowers = followersCount;

    setIsFollowingState(!previousState);
    setFollowersCount((prev) => prev + (previousState ? -1 : 1));

    try {
      if (previousState) {
        await unfollowUser(targetUid).unwrap();
      } else {
        await followUser(targetUid).unwrap();
      }
      refetchUser?.();
    } catch (error) {
      console.error("Follow/Unfollow error:", error);
      setIsFollowingState(previousState);
      setFollowersCount(previousFollowers);
      setToast({ message: "Failed to update subscription", success: false });
    }
  };

  const handleMentorClick = async (mentor: string) => {
    const actualQuery = mentor.startsWith("@") ? mentor.replace("@", "").trim() : mentor.trim();

    if (!actualQuery) return;

    try {
      const result = await searchUser({
        query: actualQuery,
        type: "users",
        limit: 1,
      }).unwrap();

      if (result.results && result.results.length > 0) {
        const user = result.results[0].data;
        navigate(`/profile/${user.uid}`);
      } else {
        setToast({ message: "User not found", success: false });
      }
    } catch (err) {
      console.error("Search error", err);
      setToast({ message: "Search error", success: false });
    }
  };

  return (
    <Box sx={styles.container}>
      {showSendModal && (
        <SendFundsZap
          onClose={() => setShowSendModal(false)}
          initialRecipient={{ uid: targetUid, username, image: avatarUrl, bio }}
        />
      )}

      <EditProfileModal
        open={editProfileModalOpen}
        handleClose={() => setEditProfileModalOpen(false)}
        onDelete={handleEditProfileSave}
      />
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <FollowersModal
        open={followersModalOpen}
        onClose={() => setFollowersModalOpen(false)}
        followersCount={followers}
        followingCount={following}
        initialTab={modalTab}
        uid={targetUid}
      />
      <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap">
        <Avatar src={avatarUrl} sx={styles.avatar} />

        <Box sx={{ flex: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            sx={{ height: "104px", display: "flex", alignItems: "center" }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                gap: "20px",
                height: "74px",
              }}
            ></Box>
            <Box sx={styles.buttonBox}>
              {Edit ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={styles.buttonEdit}
                  onClick={() => setEditProfileModalOpen(true)}
                >
                  Edit
                </Button>
              ) : (
                <>
                  {/* <Button sx={styles.buttonBuyFocuslist}>Buy Focuslist</Button> */}
                  <RequireAuth>
                    <Button
                      sx={{
                        ...styles.buttonSendZap,
                        "& .MuiButton-startIcon": {
                          marginRight: "2px",
                          marginLeft: "0px",
                        },
                      }}
                      onClick={() => setShowSendModal(true)}
                      startIcon={<SendFundsSvg />}
                    >
                      <Typography sx={styles.svgSendZap}>Send ZAP</Typography>
                    </Button>
                    <Button sx={styles.buttonFollow} onClick={handleFollowClick}>
                      {isFollowingState ? "Unfollow" : "Follow"}
                    </Button>
                  </RequireAuth>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Box flex="1 1 100%">
          <Stack direction="row" alignItems="center" gap="4px" sx={{ marginBottom: "4px" }}>
            <Typography variant="h6" sx={styles.username}>
              {`@${username}`}
            </Typography>
            {isPremium && (
              <Box sx={{ width: "20px", height: "20px" }}>
                <PremiumCheckmark />
              </Box>
            )}
            {!!topEarnerRank && <TopEarnerBadge rank={topEarnerRank} />}
          </Stack>
          {bio !== null && (
            <Typography sx={styles.bioText}>
              {(bio ?? "").split(" ").map((word, index) => {
                const isSpecial = word.startsWith("$");

                return (
                  <Typography
                    key={index}
                    component="span"
                    sx={{
                      color: isSpecial ? theme.palette.custom.mainBlue : "#2A2D31",
                      fontWeight: 500,
                      fontSize: "14px",
                      marginRight: "0px",
                    }}
                  >
                    {word}{" "}
                  </Typography>
                );
              })}
            </Typography>
          )}
          <Box display="flex" flexWrap="wrap" gap={0.75} sx={{ marginBottom: "16px" }}>
            <Chip icon={<LikesSvg />} label={`${likesCount} likes`} sx={styles.chipMetric} />
            {/* <Chip
              icon={<MonetizationOnIcon style={{ color: "#F5A623" }} />}
              label={
                <span>
                  {Number(earnings).toLocaleString("en-US")}{" "}
                  {usdPerSat && `(≈${(usdPerSat * Number(earnings)).toFixed(2)} USD)`}
                </span>
              }
              sx={styles.chipMetric}
            /> */}

            <Chip icon={<ZapsSvg />} label={`${zapCount} zaps`} sx={styles.chipMetric} />
            {totalEarned !== undefined && (
              <Chip
                icon={<DollarCoinSvg />}
                label={`$${formatMoney(totalEarned)} USDC`}
                sx={styles.dollarMetric}
              />
            )}
          </Box>
          <Box display="flex" flexDirection={"column"} gap={0.75} sx={{ marginBottom: "12px" }}>
            <Typography sx={styles.memberTranding}>
              <TrandingSvg />
              Trading Style: {tradingStyle}
            </Typography>
            <Typography sx={styles.memberSince}>
              <CalendarSvg />
              Member since {formattedMemberSince}
            </Typography>
            {tradingMentor && (
              <Box sx={styles.memberMentors}>
                <TradingMentorSvg />
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "13px",
                    lineHeight: "20px",
                    color: "#7D8690",
                  }}
                >
                  Trading Mentors:
                </Typography>
                {(tradingMentor ?? "")
                  .split(";")
                  .map((m) => m.trim())
                  .filter((m) => m)
                  .map((mentor, index, arr) => {
                    const isTag = mentor.startsWith("@");
                    const isLast = index === arr.length - 1;
                    return (
                      <Typography
                        key={index}
                        component="span"
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 500,
                          fontSize: "13px",
                          lineHeight: "20px",
                          color: isTag ? theme.palette.custom.mainBlue : "#2A2D31",
                          cursor: "pointer",
                        }}
                        onClick={() => handleMentorClick(mentor)}
                      >
                        {mentor}
                        {!isLast && ","}
                      </Typography>
                    );
                  })}
              </Box>
            )}
          </Box>
          {socialMedia && socialMedia.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px 10px", marginTop: "12px" }}>
              {socialMedia.map((link, index) => (
                <Typography
                  key={index}
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "18px",
                    color: "#2A85FF",
                    textDecoration: "underline",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <InsertLinkRoundedIcon
                    sx={{ fontSize: 20, color: "#9DA6B0", transform: "rotate(-45deg)" }}
                  />
                  {link.title || link.url}
                </Typography>
              ))}
            </Box>
          )}
          <Box sx={{ display: "flex", direction: "row", marginTop: "12px", gap: "20px" }}>
            <Typography
              fontWeight={500}
              fontSize="14px"
              color="#9DA6B0"
              // mt={1}
              sx={{ cursor: "pointer" }}
              onClick={() => openFollowersModal("following")}
            >
              <Typography component="span" fontWeight={700} fontSize="14px" color="#2A2D31">
                {following}
              </Typography>{" "}
              Following
            </Typography>

            <Typography
              fontWeight={500}
              fontSize="14px"
              color="#9DA6B0"
              // mt={1}
              sx={{ cursor: "pointer" }}
              onClick={() => openFollowersModal("followers")}
            >
              <Typography component="span" fontWeight={700} fontSize="14px" color="#2A2D31">
                {followersCount}
              </Typography>{" "}
              Followers
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
